import { useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Heading,
  Icon,
  Input,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@eluve/components';

import {
  type BundleDefaultRow,
  BundleDefaultRowForm,
  type TemplateBundleOption,
} from './BundleDefaultRowForm';
import {
  type BundleDefaultListItem,
  BundleDefaultsList,
} from './BundleDefaultsList';
import { BundleDefaultsPanelHeader } from './BundleDefaultsPanelHeader';
import {
  type BundleDefaultDisplayRow,
  DeleteBundleDefaultDialog,
} from './DeleteBundleDefaultDialog';
import { PersonalSettingsPane } from './PersonalSettingsPane';
import {
  getTenantTemplateBundleDefaultsQuery,
  getTenantUsersForPersonalSettingsQuery,
} from './bundle-defaults.operations';
import { getTemplateBundleDisplayName } from './template-bundle.operations';

interface TenantBundleDefaultsSectionProps {
  tenantId: string;
  tenantName?: string;
  enabledBundles: TemplateBundleOption[];
}

type SelectedScope =
  | { kind: 'tenant' }
  | {
      kind: 'user';
      userId: string;
      userName: string;
      currentBundleId: string | null;
    };

const ROW_BASE = 'cursor-pointer border-b border-border-tertiary px-3 py-2.5';

const getRowClasses = (isSelected: boolean) =>
  cn(
    ROW_BASE,
    isSelected
      ? 'bg-bg-accent-strong-primary text-white'
      : 'hover:bg-bg-tertiary',
  );

export const TenantBundleDefaultsSection: React.FC<
  TenantBundleDefaultsSectionProps
> = ({ tenantId, tenantName, enabledBundles }) => {
  const { data, refetch } = useQuery(getTenantTemplateBundleDefaultsQuery, {
    variables: { tenantId },
    context: eluveAdminHasuraContext,
  });
  const { data: usersData } = useQuery(getTenantUsersForPersonalSettingsQuery, {
    variables: { tenantId },
    context: eluveAdminHasuraContext,
  });

  const [selectedScope, setSelectedScope] = useState<SelectedScope>({
    kind: 'tenant',
  });
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<'closed' | 'add' | 'edit'>('closed');
  const [deletingRow, setDeletingRow] = useState<
    (BundleDefaultDisplayRow & { scopeLabel: string }) | null
  >(null);
  const [userSearch, setUserSearch] = useState('');

  const tenantDefaults = data?.tenantDefaults ?? [];

  const tenantUsers = useMemo(
    () =>
      (usersData?.tenantsByPk?.tenantUsers ?? []).map((tu) => ({
        userId: tu.userId,
        firstName: tu.user?.firstName ?? null,
        lastName: tu.user?.lastName ?? null,
        email: tu.user?.email ?? null,
        currentBundleId: tu.settings?.defaultTemplateBundleId ?? null,
      })),
    [usersData],
  );

  // Keep the currently-selected user's `currentBundleId` in sync with the
  // latest query data (e.g. after the personal-settings mutation refetches).
  // Without this the PersonalSettingsPane would render against stale state
  // until the admin re-selects the user.
  const liveSelectedScope = useMemo<SelectedScope>(() => {
    if (selectedScope.kind !== 'user') return selectedScope;
    const fresh = tenantUsers.find((u) => u.userId === selectedScope.userId);
    if (!fresh) return selectedScope;
    return {
      ...selectedScope,
      currentBundleId: fresh.currentBundleId,
    };
  }, [selectedScope, tenantUsers]);

  const displayedUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return tenantUsers;
    return tenantUsers.filter((u) =>
      [u.firstName, u.lastName, u.email]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [tenantUsers, userSearch]);

  const listItems = useMemo<BundleDefaultListItem[]>(
    () =>
      tenantDefaults.map((row) => ({
        id: row.id,
        bundleDisplayName: row.template_bundle
          ? getTemplateBundleDisplayName(row.template_bundle)
          : 'Unknown',
        specialty: row.specialty ?? null,
        language: row.language ?? null,
      })),
    [tenantDefaults],
  );

  const selectedRow = useMemo<BundleDefaultRow | null>(() => {
    if (!selectedRowId) return null;
    const match = tenantDefaults.find((r) => r.id === selectedRowId);
    if (!match) return null;
    return {
      id: match.id,
      tenantId: match.tenantId ?? null,
      templateBundleId: match.templateBundleId,
      specialty: match.specialty ?? null,
      language: match.language ?? null,
    };
  }, [selectedRowId, tenantDefaults]);

  const handleSelectScope = (scope: SelectedScope) => {
    setSelectedScope(scope);
    setSelectedRowId(null);
    setFormMode('closed');
  };

  const handleSelectRow = (row: BundleDefaultListItem) => {
    setSelectedRowId(row.id);
    setFormMode('edit');
  };

  const handleAdd = () => {
    setSelectedRowId(null);
    setFormMode('add');
  };

  const handleSaved = () => {
    setFormMode('closed');
    setSelectedRowId(null);
    void refetch();
  };

  const handleCancel = () => {
    setFormMode('closed');
    setSelectedRowId(null);
  };

  const handleRequestDelete = () => {
    if (!selectedRow) return;
    const source = tenantDefaults.find((r) => r.id === selectedRow.id);
    if (!source) return;
    setDeletingRow({
      id: source.id,
      templateBundleId: source.templateBundleId,
      bundleDisplayName: source.template_bundle
        ? getTemplateBundleDisplayName(source.template_bundle)
        : 'Unknown',
      specialty: source.specialty ?? null,
      language: source.language ?? null,
      updatedAt: source.updatedAt,
      scopeLabel: 'Tenant',
    });
  };

  const handleDeleted = () => {
    setDeletingRow(null);
    setFormMode('closed');
    setSelectedRowId(null);
    void refetch();
  };

  const scopeIsTenant = liveSelectedScope.kind === 'tenant';

  return (
    <Stack gap={2} align="stretch">
      <Stack gap={1} align="start">
        <Heading size="lg">Bundle Defaults</Heading>
        <Text size="sm" color="secondary">
          Organization-level defaults the resolver falls back to, and each
          user&apos;s personal default that takes higher priority when set.
        </Text>
      </Stack>

      <Stack
        align="stretch"
        className="max-h-[70vh] min-h-[500px] rounded-lg border border-border-tertiary"
        direction="horizontal"
      >
        {/* Left — Scope Selector */}
        <Stack
          className="w-1/5 shrink-0 overflow-y-auto border-r border-border-tertiary"
          gap={0}
          align="stretch"
        >
          <BundleDefaultsPanelHeader
            title="Select Scope"
            cornerRadius="tl"
            sticky
          />

          <div
            className={getRowClasses(scopeIsTenant)}
            onClick={() => handleSelectScope({ kind: 'tenant' })}
            role="button"
            aria-pressed={scopeIsTenant}
            aria-label="Select Organization Level scope"
          >
            <Stack gap={2} align="start" direction="horizontal">
              <Icon
                name="Building2"
                className={cn(
                  'mt-0.5 shrink-0',
                  scopeIsTenant ? 'text-white' : 'text-text-secondary',
                )}
              />
              <Stack align="start">
                <Text
                  inline
                  size="xs"
                  weight={scopeIsTenant ? 'semibold' : 'medium'}
                  className={scopeIsTenant ? 'text-white' : undefined}
                >
                  Organization Level
                </Text>
                <Text
                  inline
                  size="xs"
                  color={scopeIsTenant ? undefined : 'tertiary'}
                  className={scopeIsTenant ? 'text-white/80' : undefined}
                >
                  {tenantName ?? 'Tenant'}
                </Text>
              </Stack>
            </Stack>
          </div>

          <div className="border-b border-border-tertiary px-3 py-2">
            <Text size="xs" color="tertiary">
              Customize per User
            </Text>
          </div>

          <div className="px-3 py-2">
            <Input
              placeholder="Search users…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>

          <Stack align="stretch" className="overflow-y-auto">
            {displayedUsers.map((u) => {
              const isSelected =
                liveSelectedScope.kind === 'user' &&
                liveSelectedScope.userId === u.userId;
              const displayName =
                [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                u.email ||
                u.userId;
              const hasPersonal = u.currentBundleId !== null;
              return (
                <div
                  key={u.userId}
                  className={cn(getRowClasses(isSelected), 'last:border-b-0')}
                  onClick={() =>
                    handleSelectScope({
                      kind: 'user',
                      userId: u.userId,
                      userName: displayName,
                      currentBundleId: u.currentBundleId,
                    })
                  }
                  role="button"
                  aria-pressed={isSelected}
                  aria-label={`Select user scope: ${displayName}`}
                >
                  <Stack
                    gap={2}
                    justify="between"
                    align="center"
                    direction="horizontal"
                  >
                    <Stack
                      gap={2}
                      align="start"
                      className="min-w-0 flex-1"
                      direction="horizontal"
                    >
                      <Icon
                        name="User"
                        className={cn(
                          'mt-0.5 shrink-0',
                          isSelected ? 'text-white' : 'text-text-secondary',
                        )}
                      />
                      <Stack align="start" className="min-w-0">
                        <Text
                          inline
                          size="xs"
                          weight={isSelected ? 'semibold' : 'regular'}
                          className={cn(
                            'line-clamp-2',
                            isSelected && 'text-white',
                          )}
                        >
                          {displayName}
                        </Text>
                        {u.email && displayName !== u.email && (
                          <Text
                            inline
                            size="xs"
                            color={isSelected ? undefined : 'tertiary'}
                            className={isSelected ? 'text-white/80' : undefined}
                          >
                            {u.email}
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                    {hasPersonal && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Icon
                            name="Star"
                            size="sm"
                            className={cn(
                              'shrink-0',
                              isSelected ? 'text-white' : 'text-text-secondary',
                            )}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          Has a personal default bundle set
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </Stack>
                </div>
              );
            })}
            {displayedUsers.length === 0 && (
              <Stack className="px-3 py-4" gap={2}>
                <Text size="xs" color="tertiary">
                  No users match search
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>

        {scopeIsTenant ? (
          <>
            {/* Middle — Tenant defaults list */}
            <Stack
              className="w-1/4 shrink-0 overflow-y-auto border-r border-border-tertiary"
              align="stretch"
            >
              <BundleDefaultsPanelHeader
                sticky
                title="Defaults"
                action={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAdd}
                        aria-label="Add Default"
                      >
                        <Icon name="Plus" size="auto" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Add Default</TooltipContent>
                  </Tooltip>
                }
              />

              <BundleDefaultsList
                rows={listItems}
                selectedId={selectedRowId}
                onSelect={handleSelectRow}
                emptyMessage="No tenant defaults configured"
              />
            </Stack>

            {/* Right — Tenant detail / form */}
            <Stack className="flex-1 overflow-y-auto" align="stretch">
              <BundleDefaultsPanelHeader
                sticky
                cornerRadius="tr"
                title="Details"
              />

              {formMode === 'closed' ? (
                <Stack
                  className="flex-1"
                  align="center"
                  justify="center"
                  gap={2}
                >
                  <Text size="xs" color="tertiary">
                    {listItems.length === 0
                      ? 'Click + to add a default'
                      : 'Select a default to view details, or click + to add one'}
                  </Text>
                </Stack>
              ) : (
                <Stack className="p-4" gap={3} align="stretch">
                  <BundleDefaultRowForm
                    // Remount the form whenever the target row changes so its
                    // useState-initialized local fields reset to the new
                    // initialValue. Without a key, switching from one row to
                    // another would render the previous row's values.
                    key={
                      formMode === 'edit' && selectedRow
                        ? `edit:${selectedRow.id}`
                        : 'add'
                    }
                    scope={{ kind: 'tenant', tenantId }}
                    bundles={enabledBundles}
                    initialValue={
                      formMode === 'edit' && selectedRow
                        ? selectedRow
                        : undefined
                    }
                    onSaved={handleSaved}
                    onCancel={handleCancel}
                  />
                  {formMode === 'edit' && selectedRow && (
                    <Stack direction="horizontal" justify="end">
                      <Button
                        variant="destructive"
                        onClick={handleRequestDelete}
                        aria-label="Delete this default"
                      >
                        <Icon name="Trash" size="auto" />
                        Delete
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </>
        ) : (
          // User scope — single Personal Settings pane spans the remaining
          // width. Keyed by userId so switching users remounts the pane,
          // resetting the dropdown's local state to the freshly-selected
          // user's `defaultTemplateBundleId`.
          <Stack className="flex-1 overflow-y-auto" align="stretch">
            <PersonalSettingsPane
              key={liveSelectedScope.userId}
              tenantId={tenantId}
              userId={liveSelectedScope.userId}
              userName={liveSelectedScope.userName}
              currentBundleId={liveSelectedScope.currentBundleId}
              bundles={enabledBundles}
            />
          </Stack>
        )}
      </Stack>

      {deletingRow && (
        <DeleteBundleDefaultDialog
          open
          onOpenChange={(o) => !o && setDeletingRow(null)}
          row={deletingRow}
          onDeleted={handleDeleted}
        />
      )}
    </Stack>
  );
};
